import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import useSort from '../hooks/useSort';
import { summarizeIgnoredColumns } from '../utils/updateByIdNotices';

const API = process.env.REACT_APP_API_URL;

const labelStyle = { fontSize: '12px', color: '#555', marginBottom: '4px', display: 'block', fontWeight: '600' };
const thBlue = { padding: '7px 12px', textAlign: 'left', borderBottom: '1px solid #aed6f1', fontWeight: '600', color: '#1a6da8', fontSize: '12px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
const tblCell = { padding: '6px 12px', borderBottom: '1px solid #eaf2fb', fontSize: '12px', fontFamily: 'monospace', color: '#333' };
const tblCellLabel = { ...tblCell, fontFamily: 'sans-serif', color: '#555' };

const VALID_STATUSES = ['Pending Review', 'Under Review', 'DemandArc Accepted', 'Client Accepted', 'Rejected'];

function LeadUploadError({ error }) {
  const message = typeof error === 'object' ? error.message : error;
  const errors = typeof error === 'object' ? error.errors : null;
  const validValues = typeof error === 'object' ? error.valid_values : null;

  return (
    <div style={{ marginTop: '16px', background: '#fdedec', border: '1px solid #e74c3c', borderRadius: '6px', padding: '14px' }}>
      <div style={{ color: '#c0392b', fontWeight: '600', fontSize: '13px', marginBottom: errors ? '10px' : 0 }}>
        ❌ {message}
      </div>
      {errors && errors.length > 0 && (
        <div style={{ marginBottom: validValues ? '12px' : 0 }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#922b21', marginBottom: '4px' }}>Row errors:</div>
          <div style={{ background: '#fff5f5', border: '1px solid #f5b7b1', borderRadius: '4px', padding: '8px', fontSize: '11px', color: '#922b21', maxHeight: '140px', overflowY: 'auto' }}>
            {errors.map((e, i) => <div key={i}>{e}</div>)}
          </div>
        </div>
      )}
      {validValues && validValues.length > 0 && (
        <div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '6px' }}>Valid Program / Component / Goal IDs:</div>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '11px', background: 'white', borderRadius: '4px', overflow: 'hidden', border: '1px solid #ddd' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                {['Program ID', 'Component ID', 'Goal ID', 'Component Name', 'Goal Name'].map(h => (
                  <th key={h} style={{ padding: '5px 8px', textAlign: 'left', borderBottom: '1px solid #ddd', color: '#555', fontWeight: '600' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {validValues.map(v => (
                <tr key={v.goal_id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{v.program_id}</td>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{v.component_id}</td>
                  <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{v.goal_id}</td>
                  <td style={{ padding: '4px 8px' }}>{v.component_name}</td>
                  <td style={{ padding: '4px 8px' }}>{v.goal_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GoalIdTable({ goals, components, programs }) {
  const [expanded, setExpanded] = useState({});
  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const hierarchy = programs.map(prog => {
    const progComponents = components.filter(c => c.program_id === prog.id);
    const rows = [];
    for (const comp of progComponents) {
      for (const g of goals.filter(g => g.component_id === comp.id)) {
        rows.push({ program_id: prog.id, component_id: comp.id, goal_id: g.id, component_name: comp.component_name, goal_name: g.goal_name || '(unnamed)' });
      }
    }
    if (rows.length === 0) return null;
    return { program: prog, rows };
  }).filter(Boolean);

  if (hierarchy.length === 0) return <div style={{ fontSize: '12px', color: '#aaa' }}>No goals found.</div>;

  return (
    <div style={{ marginTop: '8px' }}>
      {hierarchy.map(({ program, rows }) => (
        <ProgramGoalBlock key={program.id} program={program} rows={rows}
          expanded={!!expanded[program.id]} onToggle={() => toggleExpand(program.id)} />
      ))}
    </div>
  );
}

function ProgramGoalBlock({ program, rows, expanded, onToggle }) {
  const cols = [
    { key: 'program_id', label: 'Program ID' },
    { key: 'component_id', label: 'Component ID' },
    { key: 'goal_id', label: 'Goal ID' },
    { key: 'component_name', label: 'Component Name' },
    { key: 'goal_name', label: 'Goal Name' },
  ];
  const { sorted, toggle, ind } = useSort(rows, 'component_name');

  return (
    <div style={{ marginBottom: '6px', border: '1px solid #aed6f1', borderRadius: '6px', overflow: 'hidden' }}>
      <div onClick={onToggle}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 12px', background: '#d6eaf8', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: '#1a6da8' }}>
        <span>{program.program_name} <span style={{ fontFamily: 'monospace', fontWeight: '400', fontSize: '11px' }}>(Program ID: {program.id})</span></span>
        <span style={{ fontSize: '11px', color: '#5d9fd4' }}>{expanded ? '▲ Hide' : '▼ Show'}</span>
      </div>
      {expanded && (
        <table style={{ borderCollapse: 'collapse', width: '100%', background: 'white' }}>
          <thead>
            <tr style={{ background: '#eaf4fb' }}>
              {cols.map(c => (
                <th key={c.key} style={thBlue} onClick={() => toggle(c.key)}>{c.label}{ind(c.key)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.goal_id}>
                <td style={tblCell}>{r.program_id}</td>
                <td style={tblCell}>{r.component_id}</td>
                <td style={tblCell}>{r.goal_id}</td>
                <td style={tblCellLabel}>{r.component_name}</td>
                <td style={tblCellLabel}>{r.goal_name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function LeadUpload() {
  const [goals, setGoals] = useState([]);
  const [components, setComponents] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const fileRef = useRef();

  const [fieldFile, setFieldFile] = useState(null);
  const [fieldUpdating, setFieldUpdating] = useState(false);
  const [fieldResult, setFieldResult] = useState(null);
  const fieldFileRef = useRef();

  useEffect(() => {
    axios.get(`${API}/goals/`).then(r => setGoals(r.data)).catch(() => {});
    axios.get(`${API}/components/`).then(r => setComponents(r.data)).catch(() => {});
    axios.get(`${API}/programs/`).then(r => setPrograms(r.data)).catch(() => {});
    loadHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = () => {
    setHistoryLoading(true);
    axios.get(`${API}/leads/uploads`)
      .then(r => { setHistory(r.data); setHistoryLoading(false); })
      .catch(() => setHistoryLoading(false));
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/leads/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      loadHistory();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        // Structured error from ID validation: { message, errors, valid_values }
        setResult({ error: detail });
      } else if (Array.isArray(detail)) {
        // FastAPI 422 — detail is a list of field validation errors
        const msg = detail.map(e => e.msg || JSON.stringify(e)).join('; ');
        setResult({ error: { message: msg || 'Validation error — check file format' } });
      } else {
        // String detail, null, or no response at all
        setResult({ error: { message: detail || 'Upload failed. Please check your file and try again.' } });
      }
    }
    setUploading(false);
  };

  const handleFieldUpdate = async () => {
    if (!fieldFile) return;
    setFieldUpdating(true);
    setFieldResult(null);
    try {
      const formData = new FormData();
      formData.append('file', fieldFile);
      const res = await axios.post(`${API}/leads/update-fields-by-lead-id`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFieldResult(res.data);
      setFieldFile(null);
      if (fieldFileRef.current) fieldFileRef.current.value = '';
      loadHistory();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join('; ') : detail;
      setFieldResult({ error: msg || 'Update failed. Please check your file and try again.' });
    }
    setFieldUpdating(false);
  };

  const downloadCsv = async (uploadId, filename) => {
    const res = await axios.get(`${API}/leads/uploads/${uploadId}/csv`, { responseType: 'text' });
    const blob = new Blob([res.data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `upload-${uploadId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const th = { padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontSize: '12px', background: '#f5f5f5', whiteSpace: 'nowrap' };
  const td = { padding: '8px 12px', borderBottom: '1px solid #eee', fontSize: '12px' };

  return (
    <div style={{ maxWidth: '860px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>📤 Lead Status Updater</h2>
      </div>

      <div style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: '8px', padding: '24px', marginBottom: '28px' }}>

        {/* Required ID reference */}
        <div style={{ marginBottom: '20px', background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '6px', padding: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a6da8', marginBottom: '4px' }}>
            Required CSV columns
          </div>
          <div style={{ fontSize: '12px', color: '#555', marginBottom: '10px' }}>
            Every row must include{' '}
            <code style={{ background: '#dce9f7', padding: '1px 5px', borderRadius: '3px' }}>Program ID</code>,{' '}
            <code style={{ background: '#dce9f7', padding: '1px 5px', borderRadius: '3px' }}>Component ID</code>,{' '}
            <code style={{ background: '#dce9f7', padding: '1px 5px', borderRadius: '3px' }}>Goal ID</code>,{' '}
            <code style={{ background: '#dce9f7', padding: '1px 5px', borderRadius: '3px' }}>lead_id</code>,{' '}
            <code style={{ background: '#dce9f7', padding: '1px 5px', borderRadius: '3px' }}>supplier_delivery_date</code>,{' '}
            <code style={{ background: '#dce9f7', padding: '1px 5px', borderRadius: '3px' }}>status</code>.
            <br />
            <span style={{ marginTop: '4px', display: 'block' }}>
              <strong>Status values:</strong> {VALID_STATUSES.join(' · ')}
              &nbsp;&nbsp;<strong>Date format:</strong> YYYY-MM-DD, MM/DD/YYYY, or MM/DD/YY
            </span>
          </div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '4px' }}>Valid Program / Component / Goal IDs:</div>
          <GoalIdTable goals={goals} components={components} programs={programs} />
        </div>

        {/* Template download */}
        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/leads/upload-template`, { responseType: 'blob' });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement('a'); a.href = url; a.download = 'lead_upload_template.csv'; a.click();
                URL.revokeObjectURL(url);
              } catch (e) { alert('Could not download template'); }
            }}
            style={{ padding: '7px 14px', background: '#1a6da8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
          >
            ⬇ Download CSV Template
          </button>
          <span style={{ fontSize: '12px', color: '#888' }}>
            Pre-fill Goal ID from the table above to get IDs included automatically.
          </span>
        </div>

        {/* File picker */}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>CSV File</label>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={e => setFile(e.target.files[0] || null)}
            style={{ fontSize: '13px' }}
          />
          {file && (
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#555' }}>
              Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
            </div>
          )}
        </div>

        <button
          onClick={handleUpload}
          disabled={uploading || !file}
          style={{
            padding: '10px 24px', background: uploading || !file ? '#bdc3c7' : '#8e44ad',
            color: 'white', border: 'none', borderRadius: '4px', cursor: uploading || !file ? 'default' : 'pointer',
            fontWeight: '600', fontSize: '14px',
          }}
        >
          {uploading ? 'Uploading…' : '📤 Upload'}
        </button>

        {result && !result.error && (
          <div style={{ marginTop: '16px', background: '#eafaf1', border: '1px solid #a9dfbf', borderRadius: '6px', padding: '16px' }}>
            <div style={{ fontWeight: '600', color: '#1e8449', fontSize: '14px', marginBottom: '8px' }}>✓ Upload Complete</div>
            <div style={{ fontSize: '13px', color: '#2c3e50', marginBottom: '4px' }}>
              <strong>{result.leads_added}</strong> leads added, <strong>{result.leads_updated}</strong> updated
            </div>
            {result.errors && result.errors.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#c0392b', marginBottom: '4px' }}>
                  {result.errors.length} row{result.errors.length > 1 ? 's' : ''} skipped:
                </div>
                <div style={{ background: '#fdedec', border: '1px solid #f5b7b1', borderRadius: '4px', padding: '8px', fontSize: '11px', color: '#922b21', maxHeight: '120px', overflowY: 'auto' }}>
                  {result.errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              </div>
            )}
          </div>
        )}
        {result?.error && <LeadUploadError error={result.error} />}
      </div>

      {/* Update fields by NeTRA ID */}
      <div style={{ background: '#f9f9f9', border: '1px solid #eee', borderRadius: '8px', padding: '24px', marginBottom: '28px' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: '#2c3e50' }}>🔄 Update Lead Data by NeTRA ID</h3>
        <div style={{ marginBottom: '20px', background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: '6px', padding: '14px' }}>
          <div style={{ fontSize: '12px', color: '#555' }}>
            Fill in or correct field values on leads that already exist in NeTRA — matched purely by{' '}
            <strong>NeTRA Lead ID</strong>, no Program/Component/Goal/Supplier Delivery Date/Status columns needed.
            Just include a{' '}
            <code style={{ background: '#dce9f7', padding: '1px 5px', borderRadius: '3px' }}>NeTRA Lead ID</code>{' '}
            column plus whichever field(s) you're updating (e.g. LinkedIn Profile, NAICS Code) — any other columns are ignored.
            <br />
            <span style={{ marginTop: '4px', display: 'block' }}>
              <strong>Overwrites, doesn't merge:</strong> whatever value is in the file replaces the lead's current
              value for that field, even a blank cell. This does <strong>not</strong> change a lead's workflow status —
              use the main updater above or Client Leads Delivery's "Check Feedback" for that.
            </span>
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>CSV File</label>
          <input
            ref={fieldFileRef}
            type="file"
            accept=".csv,text/csv"
            onChange={e => setFieldFile(e.target.files[0] || null)}
            style={{ fontSize: '13px' }}
          />
          {fieldFile && (
            <div style={{ marginTop: '6px', fontSize: '12px', color: '#555' }}>
              Selected: <strong>{fieldFile.name}</strong> ({(fieldFile.size / 1024).toFixed(1)} KB)
            </div>
          )}
        </div>

        <button
          onClick={handleFieldUpdate}
          disabled={fieldUpdating || !fieldFile}
          style={{
            padding: '10px 24px', background: fieldUpdating || !fieldFile ? '#bdc3c7' : '#16a085',
            color: 'white', border: 'none', borderRadius: '4px', cursor: fieldUpdating || !fieldFile ? 'default' : 'pointer',
            fontWeight: '600', fontSize: '14px',
          }}
        >
          {fieldUpdating ? 'Updating…' : '🔄 Update Fields'}
        </button>

        {fieldResult && !fieldResult.error && (
          <div style={{ marginTop: '16px', background: fieldResult.updated > 0 ? '#eafaf1' : '#fef5e7', border: `1px solid ${fieldResult.updated > 0 ? '#a9dfbf' : '#f8c471'}`, borderRadius: '6px', padding: '16px' }}>
            {fieldResult.updated > 0 ? (
              <div style={{ fontWeight: '600', color: '#1e8449', fontSize: '14px', marginBottom: '8px' }}>✓ Update Complete</div>
            ) : (
              <div style={{ fontWeight: '600', color: '#b9770e', fontSize: '14px', marginBottom: '8px' }}>⚠ Nothing was updated</div>
            )}
            <div style={{ fontSize: '13px', color: '#2c3e50', marginBottom: '4px' }}>
              <strong>{fieldResult.updated}</strong> lead{fieldResult.updated === 1 ? '' : 's'} updated
            </div>
            {summarizeIgnoredColumns(fieldResult).note && (
              <div style={{ marginTop: '10px', background: '#fdedec', border: '1px solid #f5b7b1', borderRadius: '4px', padding: '10px', fontSize: '12px', color: '#922b21' }}>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                  Status was NOT changed. These columns were ignored:
                </div>
                <div style={{ fontFamily: 'monospace', marginBottom: '6px' }}>
                  {summarizeIgnoredColumns(fieldResult).statusColumns.join(', ')}
                </div>
                {summarizeIgnoredColumns(fieldResult).note}
              </div>
            )}
            {summarizeIgnoredColumns(fieldResult).identifierColumns.length > 0 && (
              <div style={{ marginTop: '8px', fontSize: '11px', color: '#7f8c8d' }}>
                Also ignored (this tool never changes them): {summarizeIgnoredColumns(fieldResult).identifierColumns.join(', ')}
              </div>
            )}
            {fieldResult.unrecognized_columns && fieldResult.unrecognized_columns.length > 0 && (
              <div style={{ marginTop: '10px', background: '#fef5e7', border: '1px solid #f8c471', borderRadius: '4px', padding: '10px', fontSize: '12px', color: '#7e5109' }}>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                  These columns were not recognized, so they were NOT applied:
                </div>
                <div style={{ fontFamily: 'monospace', marginBottom: '6px' }}>
                  {fieldResult.unrecognized_columns.map(c => `${c.column} (${c.rows} row${c.rows === 1 ? '' : 's'})`).join(', ')}
                </div>
                Check the header spelling. A field's accepted headers show as the ⓘ next to it in Lead QA Review's
                column picker, and a program's own client field names (set in QA Rules → Required Fields) also work.
              </div>
            )}
            {fieldResult.not_found && fieldResult.not_found.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#c0392b', marginBottom: '4px' }}>
                  {fieldResult.not_found.length} NeTRA Lead ID{fieldResult.not_found.length > 1 ? 's' : ''} not found:
                </div>
                <div style={{ background: '#fdedec', border: '1px solid #f5b7b1', borderRadius: '4px', padding: '8px', fontSize: '11px', color: '#922b21', maxHeight: '120px', overflowY: 'auto', fontFamily: 'monospace' }}>
                  {fieldResult.not_found.map((id, i) => <div key={i}>{id}</div>)}
                </div>
              </div>
            )}
            {fieldResult.errors && fieldResult.errors.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#c0392b', marginBottom: '4px' }}>
                  {fieldResult.errors.length} row{fieldResult.errors.length > 1 ? 's' : ''} skipped:
                </div>
                <div style={{ background: '#fdedec', border: '1px solid #f5b7b1', borderRadius: '4px', padding: '8px', fontSize: '11px', color: '#922b21', maxHeight: '120px', overflowY: 'auto' }}>
                  {fieldResult.errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              </div>
            )}
          </div>
        )}
        {fieldResult?.error && (
          <div style={{ marginTop: '16px', background: '#fdedec', border: '1px solid #e74c3c', borderRadius: '6px', padding: '14px', color: '#c0392b', fontWeight: '600', fontSize: '13px' }}>
            ❌ {fieldResult.error}
          </div>
        )}
      </div>

      {/* Upload history */}
      <div>
        <h3 style={{ margin: '0 0 14px', fontSize: '16px', color: '#2c3e50' }}>Upload History</h3>
        {historyLoading ? (
          <div style={{ color: '#aaa', fontSize: '13px' }}>Loading…</div>
        ) : history.length === 0 ? (
          <div style={{ color: '#aaa', fontSize: '13px' }}>No uploads yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={th}>Date</th>
                  <th style={th}>File</th>
                  <th style={th}>By</th>
                  <th style={{ ...th, textAlign: 'right' }}>Added</th>
                  <th style={{ ...th, textAlign: 'right' }}>Updated</th>
                  <th style={th}>CSV</th>
                </tr>
              </thead>
              <tbody>
                {history.map(u => (
                  <tr key={u.id}>
                    <td style={td}>{new Date(u.upload_date).toLocaleString()}</td>
                    <td style={td}>{u.filename || '—'}</td>
                    <td style={td}>{u.uploaded_by_name || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#27ae60', fontWeight: '600' }}>{u.leads_added}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#3498db', fontWeight: '600' }}>{u.leads_updated}</td>
                    <td style={td}>
                      <button
                        onClick={() => downloadCsv(u.id, u.filename)}
                        style={{ padding: '3px 10px', background: '#ecf0f1', border: '1px solid #bdc3c7', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', color: '#2c3e50' }}
                      >
                        ⬇ CSV
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
